/**
 * # main app settings
 *
 * ## .env
 * > same vars defined in .env will override those defined here
 * 
 * @todo improve .env
 * for now you need to add .env vars in `window.__ENV`
 * either here (see below) or in `/public/index_vue.php`
 *
 * @author Nicolas Husson <nicolas@kffein.com>
 */
import { fromPairs, map, merge } from 'lodash';
import dotEnv from './env';
import locales from './locales';
import manifest from './manifest';

/** @argument {Array} NAME - app name
 * - used for many things like cookies names etc
 * - must be auto-updated by yeoman '<%= folderName %>'
 */
const { name } = manifest

/** return a key/value object of current url search (?hkj&iuji) */
export function parseUriArgs() {
  if (!window?.location?.search) return
  return fromPairs(
    map(window.location.search.slice(1).split('&'), (arg: string) => arg.split('='))
  )
}

export const IS_DEV = process.env.NODE_ENV === 'development';

const settings = {
  locales,
  /** @argument {Array} cookies - list of cookies names, please keep locale */
  cookies: {
    locale: `${name}_locale`, // for first redirect on path `/` - remember last locale from user 
    token: `${name}_locale_jwt_token`, // for user login system
    loader: `${name}_loader`, // show main loader only 1 time.
  },

  /** @argument {Object} server - is server env - must no init ui stuff (window scroll etc) */
  server: false,
  /** @argument {Object} client - is client env */
  client: true,
  /** **DEPRECATED** please use `withMotion()` - more acurate test
   * @argument {Object} motion - is client with motion
   * use this before init tweens - but you can init scroll etc without if client */
  motion: true,
  withMotion() {
    if (settings.motion && settings.storybook) {
      const args = parseUriArgs()
      // @ts-expect-error
      if (args.motion) return true
      // @ts-expect-error
      if (args.viewMode === 'docs') return false
    }
    return settings.motion
  },
  /** if storybook - handle motion with care */
  storybook: false,

  /** may show a preloader (ui) - but let's try without 🚀 */
  SHOW_LOADER: true,
  /** if we want to display the loader only 1 time. (save cookie for this) */
  ONCE_LOADER: false,

  // activate custom scroll
  smoothScroll: true,

  /** if we want throwing errors for Kontrol when VITE_KONTROLS = false */
  kontrolErrors: false,

  /** @argument {String} VITE_SITE_URL - EXPECTED TO BE DEFINED IN .env */
  VITE_SITE_URL: `http://localhost:3000`,
  // VITE_SITE_URL: ((env) => {
  //     switch (env) {
  //       case 'development': return 'http://yoursite.kffein';
  //       default: return 'http://yoursite.dev.kffein.work'; 
  //     }
  //   })(import.meta.env.NODE_ENV),
  /** @argument {String} API_PREFIX - to construct VITE_API_URL if not defined */
  API_PREFIX: 'api',
  /** @argument {String} VITE_API_URL - default to VITE_SITE_URL/API_PREFIX */
  VITE_API_URL: '',

  NAME: manifest.name,
  IS_DEV,
  ...dotEnv,
  parseUriArgs
}

/**
* merge Setting with those injected in dom `window.__ENV`
*/
if (typeof (window) !== 'undefined') {
  (() => {
    // OVERWRITE PHP ENV (index_vue.php)
    if (Object.prototype.hasOwnProperty.call(window, '__ENV')) {
      const { __ENV } = (window as any)
      merge(settings, __ENV);
    }

    // SSR friendly
    // const isPrerender = /PhantomJS/.test(window.navigator.userAgent) -> user agent settings for prerender are here -> https://dashboard.prerender.io/settings#?tab=general
    const isPrerender = window.isPrerender || navigator.userAgent.toLowerCase().indexOf('prerender') !== -1;
    if (isPrerender) {
      settings.client = false;
      settings.motion = false;
      settings.server = true;
      return
    }

    if (import.meta.env.STORYBOOK) {
      settings.storybook = true
    }
    // console.debug('[config] test if storybook', {window: parseUriArgs(), cli: import.meta.env.STORYBOOK});
    // const isStorybook = true//
    // const isStoryDocsMode = parseUriArgs()?

    // no motion but isClient
    const preferReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (preferReducedMotion) {
      settings.motion = false;
    }
  })()
}

/**
* Auto contruct VITE_API_URL
*/
const { VITE_API_URL, API_PREFIX, VITE_SITE_URL } = settings
if (!VITE_API_URL && API_PREFIX && VITE_SITE_URL) {
  settings.VITE_API_URL = `${VITE_SITE_URL}/${API_PREFIX}/`;
}

/**
 * @argument {Function} getEnv - usefull to fetch any env/settings vars
 * - note that `setings` is already binded to this
 * - @param {Boolean} autoBoolean will auto format data to true/false
 */
declare type DotEnv = typeof dotEnv
declare type DotEnvKey = keyof DotEnv
export const getEnv = function (settings: DotEnv, key: DotEnvKey, autoBoolean = true, defaultValue?: number | string | boolean) {
  const value = settings[key] || import.meta.env?.[key] || undefined;

  if (IS_DEV) {
    if (!Object.prototype.hasOwnProperty.call(settings, key)) {
      console.warn(`[settings] \`${key}\` is not exposed in __ENV - check your settings.js and your .env`);
    }
  }

  if (!autoBoolean) return value;
  switch (value) {
    case 1:
    case '1':
    case 'true':
      return true;
    case 0:
    case '0':
    case 'false':
      return false;
    case undefined:
    case null:
      return defaultValue;
    default:
      return value;
  }
}.bind(null, settings)

export default settings;

// aditional root exports (for easier imports)
export const {
  client, server, motion, withMotion, storybook, kontrolErrors,
  cookies,
  smoothScroll,
  SHOW_LOADER,
  ONCE_LOADER,
  NAME
} = settings
export const DEBUG = (IS_DEV || getEnv('VITE_DEBUG')) && client
export { locales };



/**
 * SOME USEFULL SNIPETS
 */


// /** old config
//  * don't know if smart to use this now... will see later
//  */
// const todoOrRetestConfig = {
//   /** @argument {Boolean} sendSiteIdInHeader - ad dheader wyith siteid for api */
//   sendSiteIdInHeader: true,
//   /** @argument {Boolean} reloadOnSwitchLangue - if false, be sur to update all your datas */
//   reloadOnSwitchLangue: true,// ALWAYS TRUE FOR NOW
//   /**@todo handle trailing slash ? */
//   trailingSlash: false,
//   /** @argument {Object} axios - axios config on create */
//   axios: {
//     withCredentials: IS_DEV,
//   },
// }
